import { useTranslation } from "react-i18next"

import type { HostedAgentFormErrors, HostedAgentFormState } from "../utils/hosted-agents"
import type { ModelStatus } from "@/api/admin-api"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"

import {
  HOSTED_AGENT_MAX_ITERATIONS,
  HOSTED_AGENT_MAX_RUN_SECONDS,
  HOSTED_AGENT_MAX_WEB_TOOL_CALLS,
  HOSTED_AGENT_REASONING_EFFORTS,
} from "../utils/hosted-agents"

// Model tab of the hosted-agent edit wizard (HERMES Fas 5): Auto by default
// (Ö3), optional pinning to an enabled registry model, and reasoning effort
// gated on the pinned model's `supports_reasoning_efforts` feature flag.
// The "Runtime" group (#3033) exposes `runtime.max_iterations` — how many
// LLM↔tool rounds one assignment may use — and the per-run brakes from #3334,
// `max_web_tool_calls` and `max_run_seconds` (seconds, like the stored value).
// mem_limit/pids_limit stay raw-only.

type RuntimeLimitFieldProps = {
  id: string
  label: string
  help: string
  invalidMessage: string
  placeholder: number
  value: string
  invalid: boolean
  suffix?: string
  onChange: (value: string) => void
}

function RuntimeLimitField({
  id,
  label,
  help,
  invalidMessage,
  placeholder,
  value,
  invalid,
  suffix,
  onChange,
}: RuntimeLimitFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          aria-describedby={invalid ? `${id}-error` : `${id}-help`}
          aria-invalid={invalid}
          className="w-32"
          id={id}
          inputMode="numeric"
          placeholder={String(placeholder)}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix ? <span className="text-muted-foreground text-sm">{suffix}</span> : null}
      </div>
      {invalid ? (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
          id={`${id}-error`}
        >
          {invalidMessage}
        </p>
      ) : null}
      <p className="text-muted-foreground text-xs" id={`${id}-help`}>
        {help}
      </p>
    </div>
  )
}

export function HostedAgentModelTab({
  form,
  patch,
  errors,
  models,
  isLoadingModels,
}: {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  errors: HostedAgentFormErrors
  models: ModelStatus[]
  isLoadingModels: boolean
}) {
  const { t } = useTranslation()
  const enabledModels = models.filter((model) => model.enabled)
  const pinnedModel = enabledModels.find((model) => model.id === form.modelId)
  const effortsSupported =
    form.modelSelection === "auto" ||
    !pinnedModel ||
    pinnedModel.feature_flags.supports_reasoning_efforts === true

  const renderModelPicker = () => {
    if (isLoadingModels) return <Skeleton className="h-9 w-full" />
    if (enabledModels.length === 0) {
      return (
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.hostedAgents.modelTab.noModels", {
            defaultValue: "No enabled chat models are available.",
          })}
        </p>
      )
    }
    return (
      <Select value={form.modelId || undefined} onValueChange={(modelId) => patch({ modelId })}>
        <SelectTrigger id="hosted-agent-model-id">
          <SelectValue
            placeholder={t("adminDashboard.hostedAgents.modelTab.modelPlaceholder", {
              defaultValue: "Select a model",
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {enabledModels.map((model) => (
            <SelectItem key={model.id} value={model.id}>
              {model.displayName || model.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <div className="space-y-4">
      <RadioGroup
        value={form.modelSelection}
        onValueChange={(value) => {
          if (value === "auto") patch({ modelSelection: "auto", modelId: "" })
          else patch({ modelSelection: "fixed" })
        }}
      >
        <label
          className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
          htmlFor="hosted-agent-model-auto"
        >
          <RadioGroupItem className="mt-0.5" id="hosted-agent-model-auto" value="auto" />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">
              {t("adminDashboard.hostedAgents.modelTab.autoLabel", {
                defaultValue: "Auto (recommended)",
              })}
            </span>
            <span className="text-muted-foreground block text-xs">
              {t("adminDashboard.hostedAgents.modelTab.autoHelp", {
                defaultValue: "The platform picks the best enabled model for every turn.",
              })}
            </span>
          </span>
        </label>
        <label
          className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
          htmlFor="hosted-agent-model-fixed"
        >
          <RadioGroupItem className="mt-0.5" id="hosted-agent-model-fixed" value="fixed" />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium">
              {t("adminDashboard.hostedAgents.modelTab.fixedLabel", {
                defaultValue: "Pin to a specific model",
              })}
            </span>
            <span className="text-muted-foreground block text-xs">
              {t("adminDashboard.hostedAgents.modelTab.fixedHelp", {
                defaultValue: "Always use one enabled model from the registry.",
              })}
            </span>
          </span>
        </label>
      </RadioGroup>

      {form.modelSelection === "fixed" ? (
        <div className="space-y-2">
          <Label htmlFor="hosted-agent-model-id">
            {t("adminDashboard.hostedAgents.modelTab.modelLabel", { defaultValue: "Model" })}{" "}
            <span className="text-foreground">*</span>
          </Label>
          {renderModelPicker()}
          {errors.modelId ? (
            <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
              {t("adminDashboard.hostedAgents.modelTab.modelRequired", {
                defaultValue: "Pick a model or switch back to Auto.",
              })}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="hosted-agent-effort">
          {t("adminDashboard.hostedAgents.modelTab.effortLabel", {
            defaultValue: "Reasoning effort",
          })}
        </Label>
        <Select
          disabled={!effortsSupported}
          value={form.reasoningEffort}
          onValueChange={(value) =>
            patch({ reasoningEffort: value as HostedAgentFormState["reasoningEffort"] })
          }
        >
          <SelectTrigger className="w-48" id="hosted-agent-effort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOSTED_AGENT_REASONING_EFFORTS.map((effort) => (
              <SelectItem key={effort} value={effort}>
                {t(`adminDashboard.hostedAgents.effortValues.${effort}`, {
                  defaultValue: effort,
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {effortsSupported
            ? t("adminDashboard.hostedAgents.modelTab.effortHelp", {
                defaultValue: "How much thinking the model spends per turn.",
              })
            : t("adminDashboard.hostedAgents.modelTab.effortUnsupported", {
                defaultValue: "The pinned model does not support reasoning efforts.",
              })}
        </p>
      </div>

      <div className="space-y-4 border-t pt-4">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
          {t("adminDashboard.hostedAgents.modelTab.runtimeHeading", { defaultValue: "Runtime" })}
        </p>
        <RuntimeLimitField
          id="hosted-agent-max-iterations"
          invalid={Boolean(errors.runtimeMaxIterations)}
          placeholder={HOSTED_AGENT_MAX_ITERATIONS.default}
          value={form.runtimeMaxIterations}
          help={t("adminDashboard.hostedAgents.modelTab.maxIterationsHelp", {
            defaultValue:
              "How many rounds of model call plus tool calls one assignment may use before the agent must wrap up. Empty = the platform default ({{default}}). Cost is governed by the budget, not by this number — but multi-step skills need more rounds.",
            default: HOSTED_AGENT_MAX_ITERATIONS.default,
          })}
          invalidMessage={t("adminDashboard.hostedAgents.modelTab.maxIterationsInvalid", {
            defaultValue: "Enter a whole number between {{min}} and {{max}}, or leave empty.",
            min: HOSTED_AGENT_MAX_ITERATIONS.min,
            max: HOSTED_AGENT_MAX_ITERATIONS.max,
          })}
          label={t("adminDashboard.hostedAgents.modelTab.maxIterationsLabel", {
            defaultValue: "Max tool rounds per assignment",
          })}
          onChange={(value) => patch({ runtimeMaxIterations: value })}
        />
        <RuntimeLimitField
          id="hosted-agent-max-web-tool-calls"
          invalid={Boolean(errors.runtimeMaxWebToolCalls)}
          placeholder={HOSTED_AGENT_MAX_WEB_TOOL_CALLS.default}
          value={form.runtimeMaxWebToolCalls}
          help={t("adminDashboard.hostedAgents.modelTab.maxWebToolCallsHelp", {
            defaultValue:
              "How many web searches, page fetches and web images one run may use. When the limit is hit, further web lookups are refused and the agent answers with what it has found so far. Searches in the agent's own sources do not count. Empty = the platform default ({{default}}).",
            default: HOSTED_AGENT_MAX_WEB_TOOL_CALLS.default,
          })}
          invalidMessage={t("adminDashboard.hostedAgents.modelTab.maxIterationsInvalid", {
            defaultValue: "Enter a whole number between {{min}} and {{max}}, or leave empty.",
            min: HOSTED_AGENT_MAX_WEB_TOOL_CALLS.min,
            max: HOSTED_AGENT_MAX_WEB_TOOL_CALLS.max,
          })}
          label={t("adminDashboard.hostedAgents.modelTab.maxWebToolCallsLabel", {
            defaultValue: "Max web lookups per run",
          })}
          onChange={(value) => patch({ runtimeMaxWebToolCalls: value })}
        />
        <RuntimeLimitField
          id="hosted-agent-max-run-seconds"
          invalid={Boolean(errors.runtimeMaxRunSeconds)}
          placeholder={HOSTED_AGENT_MAX_RUN_SECONDS.default}
          value={form.runtimeMaxRunSeconds}
          help={t("adminDashboard.hostedAgents.modelTab.maxRunSecondsHelp", {
            defaultValue:
              "Wall-clock time one run may take. When it runs out the agent is told to answer now; a run still going shortly after is ended and its partial answer is kept. Empty = the platform default ({{default}} seconds = {{minutes}} minutes).",
            default: HOSTED_AGENT_MAX_RUN_SECONDS.default,
            minutes: HOSTED_AGENT_MAX_RUN_SECONDS.default / 60,
          })}
          invalidMessage={t("adminDashboard.hostedAgents.modelTab.maxIterationsInvalid", {
            defaultValue: "Enter a whole number between {{min}} and {{max}}, or leave empty.",
            min: HOSTED_AGENT_MAX_RUN_SECONDS.min,
            max: HOSTED_AGENT_MAX_RUN_SECONDS.max,
          })}
          label={t("adminDashboard.hostedAgents.modelTab.maxRunSecondsLabel", {
            defaultValue: "Max run time",
          })}
          suffix={t("adminDashboard.hostedAgents.modelTab.secondsUnit", {
            defaultValue: "seconds",
          })}
          onChange={(value) => patch({ runtimeMaxRunSeconds: value })}
        />
      </div>
    </div>
  )
}
